/**
 * API Configuration
 * 
 * Centralizes API URL configuration and provides utilities for API calls.
 * Uses Vite environment variables (VITE_ prefix)
 */

import axios from 'axios';

// Get API URL from environment variables
const API_URL = import.meta.env.VITE_BACKEND_URL || import.meta.env.VITE_API_URL || '';

// Validate API URL is set
if (!API_URL) {
  console.error(
    'API URL not configured. Please set VITE_BACKEND_URL in your environment.\n' +
    'For local development: VITE_BACKEND_URL=http://localhost:8001\n' +
    'For production: Set in .env.production'
  );
}

// Base URL for API calls (includes /api prefix)
export const API_BASE = `${API_URL}/api`;

// Backend URL without /api prefix (for non-API routes like uploads)
export const BACKEND_URL = API_URL;

/**
 * Build full API endpoint URL
 * @param {string} endpoint - API endpoint path (e.g., '/auth/login', '/films')
 * @returns {string} Full API URL
 */
export const apiUrl = (endpoint) => {
  // Ensure endpoint starts with /
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${API_BASE}${path}`;
};

/**
 * Build full backend URL (for non-API routes)
 * @param {string} path - Path (e.g., '/uploads/avatars/image.jpg')
 * @returns {string} Full backend URL
 */
export const backendUrl = (path) => {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${BACKEND_URL}${cleanPath}`;
};

// Create axios instance with auto-attached auth headers
const apiClient = axios.create({
  baseURL: API_BASE,
});

apiClient.interceptors.request.use((config) => {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
    const projectRef = supabaseUrl.split('//')[1]?.split('.')[0] || '';
    const storageKey = `sb-${projectRef}-auth-token`;
    const storedData = localStorage.getItem(storageKey);
    if (storedData) {
      const parsed = JSON.parse(storedData);
      if (parsed?.access_token) {
        config.headers.Authorization = `Bearer ${parsed.access_token}`;
      }
    }
  } catch (e) {
    console.error('Error setting auth headers:', e);
  }
  return config;
});

export default apiClient;
